import { useMemo } from "react";
import { Ban, Search as SearchIcon } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ROLE_OPTIONS, TIER_OPTIONS } from "./users/types";
import { useAdminUsers } from "./users/useAdminUsers";
import { UserListRow } from "./users/UserListRow";
import { UserDetailDrawer } from "./users/UserDetailDrawer";

export const UsersTab = () => {
  const { user } = useAuth();
  const { isSuperAdmin } = useUserRole();
  const callerId = user?.id ?? null;
  const s = useAdminUsers();

  const stats = useMemo(() => ({
    total: s.rows.length,
    banned: s.rows.filter((r) => r.is_banned).length,
  }), [s.rows]);

  return (
    <div className="space-y-4">
      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle>All users</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2 md:grid-cols-[1fr_180px_180px_auto]">
            <div className="relative">
              <SearchIcon className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={s.search}
                onChange={(e) => s.setSearch(e.target.value)}
                placeholder="Search by email, name, or workspace"
                className="pl-8"
              />
            </div>
            <Select value={s.tier} onValueChange={s.setTier}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIER_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={s.role} onValueChange={s.setRole}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant={s.bannedOnly ? "default" : "outline"} onClick={() => s.setBannedOnly((v) => !v)}>
              <Ban className="h-4 w-4" /> Banned only
            </Button>
          </div>

          <div className="text-xs text-muted-foreground">
            Showing {stats.total} on this page · {stats.banned} banned
            {!isSuperAdmin && " · read-only (super admin required for actions)"}
          </div>

          <div className="space-y-2">
            {s.loading && Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
            {!s.loading && s.rows.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">No users match these filters.</p>
            )}
            {!s.loading && s.rows.map((u) => (
              <UserListRow
                key={u.id}
                user={u}
                isSelf={u.id === callerId}
                onClick={() => s.setSelected(u)}
              />
            ))}
          </div>

          <div className="flex items-center justify-between pt-2">
            <Button variant="ghost" size="sm" disabled={s.page <= 1 || s.loading} onClick={() => s.setPage((p) => Math.max(1, p - 1))}>
              ← Previous
            </Button>
            <span className="text-xs text-muted-foreground">Page {s.page}</span>
            <Button variant="ghost" size="sm" disabled={!s.nextCursor || s.loading} onClick={() => s.nextCursor && s.setPage(s.nextCursor)}>
              Next →
            </Button>
          </div>
        </CardContent>
      </Card>

      <UserDetailDrawer
        selected={s.selected}
        onOpenChange={(open) => !open && s.setSelected(null)}
        callerId={callerId}
        isSuperAdmin={isSuperAdmin}
        actionBusy={s.actionBusy}
        callAction={s.callAction}
        refreshSelected={s.refreshSelected}
        onDeleted={async () => {
          s.setSelected(null);
          await s.reload();
        }}
      />
    </div>
  );
};
